import React, { memo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import classNames from 'classnames';
import styles from '../../DataExtractionTemplate.module.scss';
import EditableField from '../EditableField';
import { DataExtractionTemplateState } from '../../DataExtractionTemplate';
import {
  validate,
  NO_EQUIVALENT_VALUE,
} from '../../../components/editableFieldValidation';
import ConfirmationDialog from '../ConfirmationDialog';
import { useHidingDefaultFields } from '../../useHidingDefaultFields';
import { useCheckSuggestions } from '../../utils';
import { SparkleWithTooltip } from 'concepts/AutoDocExtraction/Sparkle/Sparkle';
import {
  Heading,
  Text,
  Icon,
  IconList,
  Link,
  PopoverTrigger,
  PopoverContent,
  Popover,
  FormControl,
  Checkbox,
} from 'components/core';
import {
  CustomFieldSupportedSuggestion,
  HiddenFieldCategory,
} from 'types/DataExtraction';

const RESERVED_FIELD_NAMES: string[] = [
  'inclusion criteria',
  'exclusion criteria',
  'group differences',
];

type Props = {
  hasExtractedData?: boolean;
  useSuggestions: boolean;
  supportedSuggestions: CustomFieldSupportedSuggestion[];
};

const PopulationPanel = ({
  hasExtractedData = false,
  useSuggestions,
  supportedSuggestions,
}: Props) => {
  const [modal, setModal] = useState<React.ReactElement | null>();
  const { getValues } = useFormContext();
  const additionalFields = useFieldArray<DataExtractionTemplateState>({
    name: 'population.additionalFields',
  });
  const { handleChange, isChecked } = useHidingDefaultFields(
    HiddenFieldCategory.POPULATION
  );
  const baselineCharacteristics = useFieldArray<DataExtractionTemplateState>({
    name: 'population.baseline_characteristics',
  });
  const MAX_LENGTH = 255;
  const sectionClassnames = classNames({
    [styles.Panel]: true,
    [styles.WithinAccordion]: true,
  });

  const checkSuggestions = useCheckSuggestions(
    supportedSuggestions,
    'population'
  );

  return (
    <section className={sectionClassnames}>
      {modal}
      <div className={styles.details}>
        <Heading type="h5" component="h4" variant="neutral">
          Population details
        </Heading>
        <ul>
          <li>
            <FormControl
              actions={
                useSuggestions && (
                  <SparkleWithTooltip label="Inclusion criteria (in the study)" />
                )
              }
            >
              <Checkbox
                name="inclusion_criteria"
                value="inclusion_criteria"
                checked={isChecked('inclusion_criteria')}
                onChange={handleChange}
              >
                <Text component="span" variant="medium">
                  Inclusion criteria (in the study)
                </Text>
              </Checkbox>
            </FormControl>
          </li>
          <li>
            <FormControl
              actions={
                useSuggestions && (
                  <SparkleWithTooltip label="Exclusion criteria (in the study)" />
                )
              }
            >
              <Checkbox
                name="exclusion_criteria"
                value="exclusion_criteria"
                checked={isChecked('exclusion_criteria')}
                onChange={handleChange}
              >
                <Text component="span" variant="medium">
                  Exclusion criteria (in the study)
                </Text>
              </Checkbox>
            </FormControl>
          </li>
          <li>
            <FormControl
              actions={
                useSuggestions && (
                  <SparkleWithTooltip label="Group differences" />
                )
              }
            >
              <Checkbox
                name="group_differences"
                value="group_differences"
                checked={isChecked('group_differences')}
                onChange={handleChange}
              >
                <Text component="span" variant="medium">
                  Group differences
                </Text>
              </Checkbox>
            </FormControl>
          </li>
        </ul>
      </div>
      <div className={styles.details}>
        <Heading type="h5" component="h4" variant="neutral">
          Additional data
        </Heading>
        {additionalFields.fields.map((field, index) => (
          <EditableField
            key={field.id}
            index={index}
            name="population.additionalFields"
            onDelete={() => {
              const { id } = getValues(`population.additionalFields.${index}`);

              if (id && hasExtractedData) {
                setModal(
                  <ConfirmationDialog
                    onConfirm={() => additionalFields.remove(index)}
                    onClose={() => setModal(null)}
                  />
                );
              } else {
                additionalFields.remove(index);
              }
            }}
            validate={(value, existingFields) => {
              return validate(
                value,
                existingFields,
                RESERVED_FIELD_NAMES,
                NO_EQUIVALENT_VALUE,
                MAX_LENGTH
              );
            }}
            suggestable={checkSuggestions}
          />
        ))}
        <Link
          onClick={() => additionalFields.append({ name: '' })}
          variant="primary"
          className={styles.AddButton}
        >
          <Icon icon={IconList.light.faPlus} /> Add additional data
        </Link>
      </div>
      <div className={styles.details}>
        <Heading type="h5" component="h4" variant="neutral">
          Baseline characteristics
        </Heading>
        <Popover closeOnBlur={true} renderCloseButton={false}>
          <Text variant="weak" className={styles.PopoverTriggerText}>
            What details do you want to collect for{' '}
            <PopoverTrigger>
              <Text component="span" className={styles.PopoverTrigger}>
                each group of participants in the study
              </Text>
            </PopoverTrigger>
            ?
          </Text>
          <PopoverContent
            aria-label="Baseline characteristics"
            className={styles.PopoverContentText}
          >
            <Text>
              When extracting data from studies, a table will be created with a
              row for each baseline characteristic and a column for each group
              reported in the study.
            </Text>
          </PopoverContent>
        </Popover>
        {baselineCharacteristics.fields.map((field, index) => (
          <EditableField
            key={field.id}
            index={index}
            name="population.baseline_characteristics"
            label="Baseline characteristic"
            onDelete={() => {
              const { id } = getValues(
                `population.baseline_characteristics.${index}`
              );

              if (id && hasExtractedData) {
                setModal(
                  <ConfirmationDialog
                    onConfirm={() => baselineCharacteristics.remove(index)}
                    onClose={() => setModal(null)}
                  />
                );
              } else {
                baselineCharacteristics.remove(index);
              }
            }}
            validate={(value, existingFields) => {
              return validate(
                value,
                existingFields,
                [],
                NO_EQUIVALENT_VALUE,
                MAX_LENGTH
              );
            }}
          />
        ))}
        <Link
          onClick={() => baselineCharacteristics.append({ name: '' })}
          variant="primary"
          className={styles.AddButton}
        >
          <Icon icon={IconList.light.faPlus} /> Add baseline characteristic
        </Link>
      </div>
    </section>
  );
};

export default memo(PopulationPanel);
